import { useEffect, useState } from "react";
import { useGlobalStateStore } from "../stores/store";
import ContentItem from "../components/ContentItem";
import SortSelect from "../components/filterComponents/SortSelect";
import SearchInput from "../components/filterComponents/SearchInput";
import ShowMore from "../components/filterComponents/ShowMore";
import ComparisonPopup from "../components/popups/ComparisonPopup";
import { Tabs } from "../model/model";
import { loadNextOddsPage } from "../components/interactors/odds";

// This component is the main page of the app
function MainPage() {
  const store = useGlobalStateStore();
  const [activeTab, setActiveTab] = useState(Tabs.FullTime);
  const [expandedLeagueId, setExpandedLeagueId] = useState(null); // Track single expanded league ID

  useEffect(() => {
    store.setLastLoadedPage(activeTab, 0); // ensure fresh start when tab is loaded
    store.setCanLoadMore(true);
    store.clearOddsData();
    loadNextOddsPage(store, { tab: activeTab });
  }, [activeTab]);

  const { data, canLoadMore } = store;
  const loadingForActiveTab = store.isLoadingForTab(activeTab);
  const paginationLoading = store.isPaginationLoading();

  const selectedBet = store.selectedBet;
  const isBetSelected = selectedBet != null;

  const onTabClick = (e) => {
    const tab = e.currentTarget.dataset.tab;
    setActiveTab(tab);
    store.setActiveTab(tab);

    store.setLastLoadedPage(tab, 0); // Reset page for the tab
    store.setCanLoadMore(true);
    // loadNextOddsPage(store, { tab }); // Load first page for new tab
  };

  const [isChromeIOS, setIsChromeIOS] = useState(false);

  useEffect(() => {
    if (document.documentElement.classList.contains("chrome-ios")) {
      setIsChromeIOS(true);
    }
  }, []);

  useEffect(() => {
    if (store.getMode() !== "selectedBet") {
      // Collapse all leagues when mode is not "selectedBet"
      setExpandedLeagueId(null);
    }
  }, [store.getMode()]);

  useEffect(() => {
    let timeoutId = null;
    if (selectedBet != null) {
      store.displaySelectedBetPopup();
      timeoutId = setTimeout(() => {
        store.hideSelectedBetPopup();
      }, 3000);
    }
    if (timeoutId != null)
      return () => {
        clearTimeout(timeoutId);
      };
  }, [selectedBet]);

  const onFilterByBetClicked = (e) => {
    e.preventDefault();
    if (!isBetSelected) return;

    const activeTab = store.getActiveTab();

    store.setMode("selectedBet");
    store.setLastLoadedPage(activeTab, 0);
    store.setCanLoadMore(false);

    store.hideSelectedBetPopup();

    loadNextOddsPage(store, {
      tab: activeTab,
      silent: true,
      onBeforeSuccess: () => {
        store.clearOddsData();
      },
    });
  };

  const [theme, setTheme] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const initialTheme = localStorage.getItem("theme") || "light";
    setTheme(initialTheme);
    setHydrated(true); // mark theme as loaded
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newTheme = localStorage.getItem("theme") || "light";
      setTheme(newTheme);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const handleToggle = (leagueId) => {
    // If the clicked league is already expanded, collapse it
    if (expandedLeagueId === leagueId) {
      setExpandedLeagueId(null);
    } else {
      // Expand the new league and collapse the previous one
      setExpandedLeagueId(leagueId);
    }
  };

  return (
    <>
      <div className="wrapper">
        <main className="page">
          <div className="main-section">
            <div className="container-fix-width">
              <div
                className={`main-section__wrap ${
                  isChromeIOS ? "chrome-ios-scroll-fix" : ""
                }`}
              >
                <div className="main-section__sort-wrap">
                  <div className="main-section__search-wrap">
                    <svg
                      className="search-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        className="vector-white-to-black"
                        d="M18.707 17.293L15.307 13.898C16.5564 12.2826 17.143 10.2518 16.9474 8.21903C16.7518 6.18628 15.7887 4.30454 14.2543 2.95703C12.7198 1.60953 10.7294 0.897611 8.68843 0.966273C6.64745 1.03494 4.7094 1.87901 3.26898 3.3266C1.82856 4.77418 0.994114 6.7164 0.935582 8.75769C0.87705 10.799 1.59884 12.7858 2.95394 14.3136C4.30904 15.8413 6.19555 16.7951 8.22924 16.9806C10.2629 17.1661 12.2908 16.5694 13.9 15.312L17.295 18.712C17.4836 18.8941 17.7362 18.9949 17.9984 18.9927C18.2606 18.9904 18.5114 18.8852 18.6968 18.6998C18.8822 18.5144 18.9874 18.2636 18.9897 18.0014C18.9919 17.7392 18.8911 17.4866 18.709 17.298L18.707 17.293ZM2.99999 8.99999C2.99999 7.81331 3.35188 6.65327 4.01117 5.66657C4.67046 4.67988 5.60753 3.91084 6.70389 3.45672C7.80025 3.00259 9.00664 2.88377 10.1705 3.11528C11.3344 3.34679 12.4035 3.91824 13.2426 4.75735C14.0817 5.59647 14.6532 6.66557 14.8847 7.82945C15.1162 8.99334 14.9974 10.1997 14.5433 11.2961C14.0891 12.3925 13.3201 13.3295 12.3334 13.9888C11.3467 14.6481 10.1867 15 8.99999 15C7.40918 14.9984 5.88398 14.3658 4.7591 13.2409C3.63423 12.116 3.00158 10.5908 2.99999 8.99999Z"
                        fill="white"
                      />
                    </svg>
                    <SearchInput />
                  </div>
                  <SortSelect />

                  {store.getMode() === "selectedBet" && hydrated ? (
                    <a
                      href="#"
                      className="main-section__comparison-icon reset"
                      onClick={(e) => {
                        e.preventDefault();

                        const activeTab = store.getActiveTab();

                        store.setMode("filter");
                        store.clearSelectedBet();
                        store.hideSelectedBetPopup();

                        store.setLastLoadedPage(activeTab, 0);
                        store.setCanLoadMore(true);

                        loadNextOddsPage(store, {
                          tab: activeTab,
                          onBeforeSuccess: () => {
                            store.clearOddsData();
                          },
                          onSuccess: (itemsLoaded) => {
                            if (itemsLoaded === 0) {
                              store.setCanLoadMore(false);
                            }
                          },
                        });
                      }}
                      title="Reset Compare Odds"
                    >
                      <svg
                        viewBox="0 -8 60 80"
                        className="reset-icon"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g transform="scale(2) translate(-10, -9)">
                          <g clip-path="url(#clip0_84_1282)">
                            <path
                              d="M20 25.7143H16.4286C16.2391 25.7143 16.0575 25.7895 15.9235 25.9235C15.7895 26.0574 15.7143 26.2391 15.7143 26.4286V33.5714C15.7143 33.7609 15.7895 33.9425 15.9235 34.0765C16.0575 34.2105 16.2391 34.2857 16.4286 34.2857H20C20.1894 34.2857 20.3711 34.2105 20.5051 34.0765C20.639 33.9425 20.7143 33.7609 20.7143 33.5714V26.4286C20.7143 26.2391 20.639 26.0574 20.5051 25.9235C20.3711 25.7895 20.1894 25.7143 20 25.7143ZM19.2857 32.8571H17.1429V27.1429H19.2857V32.8571Z"
                              fill="#FF8000"
                            />
                            <path
                              d="M26.7857 20.7143H23.2143C23.0248 20.7143 22.8432 20.7895 22.7092 20.9235C22.5753 21.0574 22.5 21.2391 22.5 21.4286V33.5714C22.5 33.7609 22.5753 33.9425 22.7092 34.0765C22.8432 34.2105 23.0248 34.2857 23.2143 34.2857H26.7857C26.9752 34.2857 27.1568 34.2105 27.2908 34.0765C27.4247 33.9425 27.5 33.7609 27.5 33.5714V21.4286C27.5 21.2391 27.4247 21.0574 27.2908 20.9235C27.1568 20.7895 26.9752 20.7143 26.7857 20.7143ZM26.0714 32.8571H23.9286V22.1429H26.0714V32.8571Z"
                              fill={theme === "dark" ? "white" : "black"}
                            />
                            <path
                              d="M26.7858 20.7143H23.2143C23.0249 20.7143 22.8432 20.7895 22.7093 20.9235C22.5753 21.0574 22.5001 21.2391 22.5001 21.4286V33.5714C22.5001 33.7609 22.5753 33.9425 22.7093 34.0765C22.8432 34.2105 23.0249 34.2857 23.2143 34.2857H26.7858C26.9752 34.2857 27.1569 34.2105 27.2909 34.0765C27.4248 33.9425 27.5001 33.7609 27.5001 33.5714V21.4286C27.5001 21.2391 27.4248 21.0574 27.2909 20.9235C27.1569 20.7895 26.9752 20.7143 26.7858 20.7143ZM26.0715 32.8571H23.9286V22.1429H26.0715V32.8571Z"
                              fill="#FF8000"
                            />
                            <path
                              d="M33.5714 15.7143H30C29.8106 15.7143 29.6289 15.7895 29.4949 15.9235C29.361 16.0574 29.2857 16.2391 29.2857 16.4286V33.5714C29.2857 33.7609 29.361 33.9425 29.4949 34.0765C29.6289 34.2105 29.8106 34.2857 30 34.2857H33.5714C33.7609 34.2857 33.9425 34.2105 34.0765 34.0765C34.2105 33.9425 34.2857 33.7609 34.2857 33.5714V16.4286C34.2857 16.2391 34.2105 16.0574 34.0765 15.9235C33.9425 15.7895 33.7609 15.7143 33.5714 15.7143ZM32.8571 32.8571H30.7143V17.1429H32.8571V32.8571Z"
                              fill="#FF8000"
                            />
                          </g>
                          <path
                            d="M10.0548 21.1328C10.9929 17.6318 13.116 14.5643 16.0622 12.4531C19.0084 10.3418 22.5955 9.31749 26.2123 9.55455C29.8291 9.7916 33.2519 11.2754 35.8973 13.7531C38.5427 16.2309 40.2472 19.5492 40.7203 23.1428C41.1934 26.7363 40.4059 30.3827 38.4918 33.4607C36.5778 36.5387 33.6558 38.8578 30.2236 40.0229C26.7914 41.188 23.0614 41.127 19.6691 39.8502C16.2768 38.5734 13.4322 36.16 11.6199 33.021"
                            stroke={theme === "dark" ? "white" : "black"}
                            stroke-width="1.00088"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7.4936 13.7046L9.52101 21.271L17.0874 19.2436"
                            stroke={theme === "dark" ? "white" : "black"}
                            stroke-width="1.00088"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_84_1282">
                            <rect
                              width="20"
                              height="20"
                              fill="white"
                              transform="translate(15 15)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </a>
                  ) : (
                    <a
                      href="#"
                      className={`${
                        isBetSelected ? "active" : ""
                      } main-section__comparison-icon`}
                      onClick={onFilterByBetClicked}
                      title="Compare Odds"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_322_1213)">
                          <path
                            d="M5.00007 10.7144H1.42864C1.2392 10.7144 1.05752 10.7896 0.923565 10.9236C0.78961 11.0575 0.714355 11.2392 0.714355 11.4286V18.5715C0.714355 18.7609 0.78961 18.9426 0.923565 19.0766C1.05752 19.2105 1.2392 19.2858 1.42864 19.2858H5.00007C5.18951 19.2858 5.37119 19.2105 5.50515 19.0766C5.6391 18.9426 5.71436 18.7609 5.71436 18.5715V11.4286C5.71436 11.2392 5.6391 11.0575 5.50515 10.9236C5.37119 10.7896 5.18951 10.7144 5.00007 10.7144ZM4.28578 17.8572H2.14293V12.1429H4.28578V17.8572Z"
                            fill="#FF8000"
                          />
                          <path
                            d="M11.7857 5.71436H8.21429C8.02485 5.71436 7.84316 5.78961 7.70921 5.92357C7.57526 6.05752 7.5 6.2392 7.5 6.42864V18.5715C7.5 18.7609 7.57526 18.9426 7.70921 19.0766C7.84316 19.2105 8.02485 19.2858 8.21429 19.2858H11.7857C11.9752 19.2858 12.1568 19.2105 12.2908 19.0766C12.4247 18.9426 12.5 18.7609 12.5 18.5715V6.42864C12.5 6.2392 12.4247 6.05752 12.2908 5.92357C12.1568 5.78961 11.9752 5.71436 11.7857 5.71436ZM11.0714 17.8572H8.92857V7.14293H11.0714V17.8572Z"
                            fill={theme === "dark" ? "white" : "black"}
                          />
                          <path
                            className="vector-white-to-black"
                            d="M11.7857 5.71436H8.21429C8.02485 5.71436 7.84316 5.78961 7.70921 5.92357C7.57526 6.05752 7.5 6.2392 7.5 6.42864V18.5715C7.5 18.7609 7.57526 18.9426 7.70921 19.0766C7.84316 19.2105 8.02485 19.2858 8.21429 19.2858H11.7857C11.9752 19.2858 12.1568 19.2105 12.2908 19.0766C12.4247 18.9426 12.5 18.7609 12.5 18.5715V6.42864C12.5 6.2392 12.4247 6.05752 12.2908 5.92357C12.1568 5.78961 11.9752 5.71436 11.7857 5.71436ZM11.0714 17.8572H8.92857V7.14293H11.0714V17.8572Z"
                            fill="white"
                          />
                          <path
                            className="vector-white-to-black"
                            d="M18.5714 0.714355H14.9999C14.8105 0.714355 14.6288 0.78961 14.4949 0.923565C14.3609 1.05752 14.2856 1.2392 14.2856 1.42864V18.5715C14.2856 18.7609 14.3609 18.9426 14.4949 19.0766C14.6288 19.2105 14.8105 19.2858 14.9999 19.2858H18.5714C18.7608 19.2858 18.9425 19.2105 19.0764 19.0766C19.2104 18.9426 19.2856 18.7609 19.2856 18.5715V1.42864C19.2856 1.2392 19.2104 1.05752 19.0764 0.923565C18.9425 0.78961 18.7608 0.714355 18.5714 0.714355ZM17.8571 17.8572H15.7142V2.14293H17.8571V17.8572Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_322_1213">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </a>
                  )}
                </div>

                <div className="main-section__content-wrap">
                  <ul className="tabs-list">
                    <li
                      className={activeTab === Tabs.FullTime ? "active" : ""}
                      data-tab={Tabs.FullTime}
                      onClick={onTabClick}
                    >
                      Full Time
                    </li>
                    <li
                      data-tab={Tabs.FirstHalf}
                      className={activeTab === Tabs.FirstHalf ? "active" : ""}
                      onClick={onTabClick}
                    >
                      First Half
                    </li>
                    <li
                      data-tab={Tabs.SecondHalf}
                      className={activeTab === Tabs.SecondHalf ? "active" : ""}
                      onClick={onTabClick}
                    >
                      Second Half
                    </li>
                  </ul>
                  <div
                    data-tab-content
                    id="full-time"
                    className={`tab-content ${
                      activeTab === Tabs.FullTime ? "" : "hidden"
                    }`}
                  >
                    <div className="main-section__content-item-wrap">
                      {loadingForActiveTab && !paginationLoading ? (
                        <div className="content-loading">
                          <div className="spinner" />
                        </div>
                      ) : (
                        data[Tabs.FullTime].map((league) => (
                          <ContentItem
                            key={league.league_id || league.league_name} // Use league_id if available, fallback to league_name
                            league={league}
                            isExpanded={
                              expandedLeagueId ===
                              (league.league_id || league.league_name)
                            }
                            onToggle={() =>
                              handleToggle(
                                league.league_id || league.league_name
                              )
                            }
                          />
                        ))
                      )}
                    </div>
                  </div>
                  <div
                    className={`tab-content ${
                      activeTab === Tabs.FirstHalf ? "" : "hidden"
                    }`}
                    data-tab-content
                    id="first-half"
                  >
                    <div className="main-section__content-item-wrap">
                      {loadingForActiveTab && !paginationLoading ? (
                        <div className="content-loading">
                          <div className="spinner" />
                        </div>
                      ) : (
                        data[Tabs.FirstHalf].map((league) => (
                          <ContentItem
                            key={league.league_id || league.league_name}
                            league={league}
                            isExpanded={
                              expandedLeagueId ===
                              (league.league_id || league.league_name)
                            }
                            onToggle={() =>
                              handleToggle(
                                league.league_id || league.league_name
                              )
                            }
                          />
                        ))
                      )}
                    </div>
                  </div>
                  <div
                    className={`tab-content ${
                      activeTab === Tabs.SecondHalf ? "" : "hidden"
                    }`}
                    data-tab-content
                    id="second-half"
                  >
                    <div className="main-section__content-item-wrap">
                      {loadingForActiveTab && !paginationLoading ? (
                        <div className="content-loading">
                          <div className="spinner" />
                        </div>
                      ) : (
                        data[Tabs.SecondHalf].map((league) => (
                          <ContentItem
                            key={league.league_id || league.league_name}
                            league={league}
                            isExpanded={
                              expandedLeagueId ===
                              (league.league_id || league.league_name)
                            }
                            onToggle={() =>
                              handleToggle(
                                league.league_id || league.league_name
                              )
                            }
                          />
                        ))
                      )}
                    </div>
                  </div>
                  {canLoadMore && <ShowMore />}
                </div>
              </div>
            </div>
            <ComparisonPopup onClick={onFilterByBetClicked} />
          </div>
        </main>
      </div>
    </>
  );
}

export default MainPage;
